import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect, useRef, useState } from 'react';
import { BaseButton } from 'components/BaseButton';
import { AccountingType, NewOrderPartyType } from 'v2-types/order';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { chargesPayeePayerForm, OrderEntryPartyContent } from './services';
import { useParams } from 'react-router-dom';
import { ChargeFormEntry } from 'views/orders/charges/components/GeneralChargesTable/types';
import { KnownPartiesList } from './components/knownPartiesList';
import { SelectedPartiesList } from './components/selectedPartiesList';
import { SearchPartyInput } from 'components/search-party-input';
import { ChargesList } from './components/chargesList';
import { getTitle } from './utils';
import { Header } from './components/header';
import { MobileStepNavigation } from './components/mobile-step-navigation';
import { MobileStep, MobileStepValue } from './mobile-step-types';

const useStyles = makeStyles((theme: any) => ({
  title: {
    fontWeight: 500,
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
    paddingBottom: theme.spacing(2),
  },
  chargesContainer: {
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
    height: '60px',
  },
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },

      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        padding: 8,
      },
    },
  },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  partyInput: {
    "& .MuiFilledInput-input": {
      padding: "10px"
    },
    "& .MuiFilledInput-underline:before": {
      border: "none !important",
      borderBottom: "none !important"
    },
    "& .MuiFilledInput-root": {
      backgroundColor: theme.palette.background.paper
    }
  },
  mobilePartySearch: { marginTop: theme.spacing(2) },
  partySearchLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.tab.offselected,
    marginBottom: theme.spacing(0.5),
  },
  containerResponsive: {
    minHeight: '400px',
    display: 'flex',
    gap: '10px',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  selectedPartiesContainer: {
    backgroundColor: theme.palette.input.border,
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'auto',
    },
  },
}));

type splitPayerPayeeProps = {
	open: boolean;
	onClose: () => void;
	charges?: ChargeFormEntry[];
	orderParties: NewOrderPartyType[];
	methods: UseFormReturn<chargesPayeePayerForm>;
	handleSave: (
		data: { accounting_id: string; parties: OrderEntryPartyContent[] }[]
	) => void;
	handleDelete: (accountingIds: string[]) => void,
	loadingSave: boolean;
	kind: 'Payee' | 'Payer' | 'Tax' | 'Proprietor' | 'Ownership';
	letter?: string;
	hideAtClosingAmount?: boolean;
	hideBeforeClosingAmount?: boolean;
	errors: string | null;
	accountingEntry?: AccountingType;
};

const SplitPayeePayerScene: FC<splitPayerPayeeProps> = ({
  open,
  onClose,
  charges,
  orderParties,
  methods,
  handleSave,
  handleDelete,
  loadingSave,
  kind,
  letter,
  hideAtClosingAmount,
  hideBeforeClosingAmount,
  errors,
  accountingEntry
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const partiesSelected = useFieldArray({ control: methods.control, name: 'InvolvedParties' });

  const [selectedChargeIds, setSelectedChargeIds] = useState<Set<string>>(
    () => new Set((charges ?? []).map((c) => c.accounting_id!).filter(Boolean))
  );

  const isMultiCharge = selectedChargeIds.size > 1;

  const selectedCharges = (charges ?? []).filter(
    (c) => c.accounting_id && selectedChargeIds.has(c.accounting_id)
  );

  const [mobileStep, setMobileStep] = useState<MobileStepValue>(() =>
    charges && charges.length > 1 ? MobileStep.SelectCharges : MobileStep.SelectParties
  );

  const handleChargeToggle = (id: string) => {
    setSelectedChargeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const goToNextStep = () => {
    const maxStep = isMultiCharge ? MobileStep.ReviewCharges : MobileStep.ConfigureAmounts;
    setMobileStep((prev) => (prev < maxStep ? (prev + 1) as MobileStepValue : prev));
  };

  const goToPrevStep = () => {
    const minStep = charges && charges.length > 1 ? MobileStep.SelectCharges : MobileStep.SelectParties;
    setMobileStep((prev) => (prev > minStep ? (prev - 1) as MobileStepValue : prev));
  };

  const [previousOpen, setPreviousOpen] = useState(false);
  if (open !== previousOpen) {
    setPreviousOpen(open);
    if (open) {
      setSelectedChargeIds(new Set((charges ?? []).map((c) => c.accounting_id!).filter(Boolean)));
      const initialStep = charges && charges.length > 1 ? MobileStep.SelectCharges : MobileStep.SelectParties;
      setMobileStep(initialStep);
    }
  }

  useEffect(() => {
    if (!open || !isMultiCharge || partiesSelected.fields.length > 0) return;

    let regex = /^(underwriter|title company)$/i;
    if (kind === 'Tax') regex = /^tax/i;
    if (kind === 'Payer') regex = /^(buyer|seller)$/i;
    if (kind === 'Proprietor') regex = /^seller/i;
    if (kind === 'Ownership') regex = /^buyer/i;

    const suggested = orderParties.filter((party) =>
      party.kinds.some((k) => regex.test(k))
    );

    if (suggested.length === 0) return;

    partiesSelected.append(
      suggested.map((party) => ({
        party_id: party._id,
        partyKinds: party.kinds,
        name: party.name,
        amount: 0,
        at_closing_amount: 0,
        before_closing_amount: 0,
        before_closing_percent: 0,
        at_closing_percent: 0,
        kind,
      }))
    );
    // Seeding is scoped to the open transition; re-running on RHF/party changes would re-append edited parties.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!charges || charges.length > 1 || charges.length === 0) return;

    if (kind === 'Payee') {
      partiesSelected.append([
        ...charges[0].payees.map((party) => ({
          party_id: party._id,
          partyKinds: party.order_kinds ?? [],
          kind,
          ...party,
          before_closing_percent: party.before_closing_percent * 100,
          at_closing_percent: party.at_closing_percent * 100,
        })),
      ]);
    }
    if (kind === 'Payer') {
      partiesSelected.append([
        ...charges[0].payers.map((party) => ({
          party_id: party._id,
          partyKinds: party.order_kinds ?? [],
          kind,
          ...party,
          before_closing_percent: party.before_closing_percent * 100,
          at_closing_percent: party.at_closing_percent * 100,
        })),
      ]);
    }
    if (kind === 'Tax') {
      partiesSelected.append([
        ...charges[0].taxes.map((party) => ({
          party_id: party._id,
          partyKinds: party.order_kinds ?? [],
          kind,
          ...party,
          before_closing_percent: party.before_closing_percent * 100,
          at_closing_percent: party.at_closing_percent * 100,
        })),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charges]);

  useEffect(() => {
    if (!accountingEntry) return;

    if (kind === 'Payee' || kind === 'Payer') {
      partiesSelected.append([
        ...accountingEntry.involved_parties
          .filter((party) => party.kind.includes(kind))
          .map((party) => ({
            party_id: party._id,
            partyKinds: party.order_kinds ?? [],
            ...party,
            kind,
            before_closing_percent: party.before_closing_percent * 100,
            at_closing_percent: party.at_closing_percent * 100,
          })),
      ]);
    }
    if (kind === 'Proprietor') {
      partiesSelected.append([
        ...accountingEntry.involved_parties
          .filter((party) => party.kind === "Proprietor")
          .map((party) => ({
            party_id: party._id,
            partyKinds: [],
            ...party,
            kind,
            before_closing_percent: party.before_closing_percent * 100,
            at_closing_percent: party.at_closing_percent * 100,
          })),
      ]);
    }
    if (kind === 'Ownership') {
      partiesSelected.append([
        ...accountingEntry.involved_parties
          .filter((party) => party.kind === "Ownership")
          .map((party) => ({
            party_id: party._id,
            partyKinds: [],
            ...party,
            kind,
            before_closing_percent: party.before_closing_percent * 100,
            at_closing_percent: party.at_closing_percent * 100,
          })),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingEntry]);

  const isFirstChargeSelectionEffect = useRef(true);

  useEffect(() => {
    if (isFirstChargeSelectionEffect.current) {
      isFirstChargeSelectionEffect.current = false;
      return;
    }

    if (selectedChargeIds.size > 1 && methods.getValues('typeAmount') === 'Amount') {
      methods.setValue('typeAmount', 'Percent');
      const firstCharge = (charges ?? []).find(
        (c) => c.accounting_id && selectedChargeIds.has(c.accounting_id)
      );
      const fullAmount = firstCharge?.amount ?? 0;
      const parties = methods.getValues('InvolvedParties');
      parties.forEach((party, index) => {
        const atAmt = party.at_closing_amount || 0;
        const beforeAmt = party.before_closing_amount || 0;
        const atPercent = fullAmount > 0 ? (atAmt / fullAmount) * 100 : 0;
        const beforePercent = fullAmount > 0 ? (beforeAmt / fullAmount) * 100 : 0;
        methods.setValue(`InvolvedParties.${index}.at_closing_percent` as any, atPercent);
        methods.setValue(`InvolvedParties.${index}.before_closing_percent` as any, beforePercent);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChargeIds]);

  const handlePartySelect = (party: NewOrderPartyType) => {
    if (!partiesSelected.fields.some(({ party_id }) => party._id === party_id)) {
      let amount = 0;
      let before_closing_amount = 0;
      let before_closing_percent = 0;

      if (partiesSelected.fields.length === 0) {
        if (charges && charges.length === 1) {
          amount = charges[0].amount;
          before_closing_amount = charges[0].amount;
        }

        if (accountingEntry) {
          amount = accountingEntry.amount;
          before_closing_amount = accountingEntry.amount;
        }

        before_closing_percent = 100;
      }

      partiesSelected.append({
        party_id: party._id,
        partyKinds: party.kinds,
        name: party.name,
        amount,
        at_closing_amount: 0,
        before_closing_amount,
        before_closing_percent,
        at_closing_percent: 0,
        kind,
      });
    }
  };

  const cleanPercent = (value: number) => {
    if (!Number.isFinite(value) || Number.isNaN(value)) return 0;
    return parseFloat(value.toFixed(10));
  };

  const safeComplement = (value: number) => {
    const v = cleanPercent(value);
    const c = cleanPercent(1 - v);
    return c < 0 ? 0 : c;
  };

  const handleOnSave = () => {
    const { handleSubmit } = methods;
    handleSubmit((data) => {
      const isMultiChargeMode = selectedChargeIds.size > 1;
      const activeParties = data.InvolvedParties.filter((party) => {
        if (isMultiChargeMode || data.typeAmount === 'Percent') {
          return (party.at_closing_percent || 0) + (party.before_closing_percent || 0) > 0;
        }
        return (party.at_closing_amount || 0) + (party.before_closing_amount || 0) > 0;
      });

      if (activeParties.length === 0) {
        let accountingIds = accountingEntry ? [accountingEntry._id] : [];
        if (accountingIds.length === 0 && charges) {
          accountingIds = selectedCharges.map((c) => c.accounting_id!);
        }
        handleDelete(accountingIds);
        return;
      }

      const dataArray = selectedCharges.length > 0 ? selectedCharges.map(({ accounting_id }) => ({
        accounting_id: accounting_id!,
        parties: activeParties.map((party) => {
          if (selectedChargeIds.size === 1) {
            if (data.typeAmount === 'Amount') {
              return {
                kind,
                party_id: party.party_id,
                amount: (party.at_closing_amount || 0) + (party.before_closing_amount || 0),
                at_closing_amount: party.at_closing_amount || 0,
                before_closing_amount: party.before_closing_amount || 0,
              };
            }

            const atAmt = party.at_closing_amount || 0;
            const beforeAmt = party.before_closing_amount || 0;
            const calculatedAmount = atAmt + beforeAmt;
            const chargeAmount = selectedCharges[0].amount || 0;
            const atClosingPct = cleanPercent(calculatedAmount > 0 ? atAmt / calculatedAmount : 0);
            return {
              at_closing_percent: atClosingPct,
              before_closing_percent: safeComplement(atClosingPct),
              kind,
              party_id: party.party_id,
              percent: cleanPercent(chargeAmount > 0 ? calculatedAmount / chargeAmount : 0),
            };
          }

          const uiAt = party.at_closing_percent || 0;
          const uiBefore = party.before_closing_percent || 0;
          const uiTotal = uiAt + uiBefore;
          const atClosingPctMulti = cleanPercent(uiTotal > 0 ? uiAt / uiTotal : 0);
          return {
            at_closing_percent: atClosingPctMulti,
            before_closing_percent: safeComplement(atClosingPctMulti),
            kind,
            party_id: party.party_id,
            percent: cleanPercent(uiTotal / 100),
          };
        }),
      })) : [];

      if (accountingEntry) {
        dataArray.push({
          accounting_id: accountingEntry._id,
          parties: activeParties.map((party) => {
            if (data.typeAmount === 'Amount') {
              return {
                kind,
                party_id: party.party_id,
                amount: (party.at_closing_amount || 0) + (party.before_closing_amount || 0),
                at_closing_amount: party.at_closing_amount || 0,
                before_closing_amount: party.before_closing_amount || 0,
              };
            }

            const atAmt = party.at_closing_amount || 0;
            const beforeAmt = party.before_closing_amount || 0;
            const calculatedAmount = atAmt + beforeAmt;
            const entryAmount = accountingEntry.amount || 0;
            const atClosingPctEntry = cleanPercent(calculatedAmount > 0 ? atAmt / calculatedAmount : 0);
            return {
              at_closing_percent: atClosingPctEntry,
              before_closing_percent: safeComplement(atClosingPctEntry),
              kind,
              party_id: party.party_id,
              percent: cleanPercent(entryAmount > 0 ? calculatedAmount / entryAmount : 0),
            };
          })
        });
      }

      handleSave(dataArray);
    })();
  };

  const title = getTitle(t, kind);

  let dialogMaxWidth: "xs" | "sm" | "md" | "lg" | "xl" = "md";
  if (!isMobile && charges && charges.length > 1) {
    dialogMaxWidth = "lg";
  }

  return (
    <Dialog
      open={open}
      onClose={loadingSave ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth={dialogMaxWidth}
    >
      <div>
        <Typography variant="h5" className={classes.title}>
          {title}
        </Typography>
      </div>
      <DialogContent style={isMobile ? { padding: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' } : { padding: 24 }}>
        {isMobile ? (
          <>
            <Box style={{ padding: '16px 16px 0', flexShrink: 0 }}>
              {errors && errors.length > 0 && (
              <Grid item xs={12} style={{ marginBottom: 12 }}>
                <Alert severity="error" classes={{ icon: classes.errorIconContainer }}>
                  <Typography variant="subtitle1">{errors}</Typography>
                </Alert>
              </Grid>
              )}
              <Header
                methods={methods}
                charge={selectedCharges.length === 1 ? selectedCharges[0] : undefined}
                accountingEntry={accountingEntry}
                isMultiCharge={isMultiCharge}
              />
            </Box>
            <Box style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
              {charges && charges.length > 1 && mobileStep === MobileStep.SelectCharges && (
              <Box data-testid="step-select-charges">
                <ChargesList
                  charges={charges}
                  selectedIds={selectedChargeIds}
                  onToggle={handleChargeToggle}
                />
              </Box>
              )}
              {mobileStep === MobileStep.SelectParties && (
              <Box data-testid="step-select-parties">
                <KnownPartiesList
                  parties={orderParties}
                  onPartySelect={handlePartySelect}
                  partiesSelected={partiesSelected}
                  kind={kind}
                />
                <Box className={classes.mobilePartySearch}>
                  <Typography variant="body2" className={classes.partySearchLabel}>
                    {t('charges:partySearchAndCreation')}
                  </Typography>
                  <SearchPartyInput
                    className={classes.partyInput}
                    clearTextOnSelect
                    onSelectParty={(party) => {
											  if (party) {
											    handlePartySelect({
											      _id: party._id,
											      name: party.name,
											      kinds: ['']
											    } as NewOrderPartyType);
											  }
                    }}
                  />
                </Box>
              </Box>
              )}
              {mobileStep === MobileStep.ConfigureAmounts && (
              <Box data-testid="step-configure-amounts" className={classes.selectedPartiesContainer}>
                <Box flex={1}>
                  <SelectedPartiesList
                    methods={methods}
                    partiesSelected={partiesSelected}
                    charges={charges}
                    accountingEntry={accountingEntry}
                    hideAtClosingAmount={hideAtClosingAmount}
                    hideBeforeClosingAmount={hideBeforeClosingAmount}
                  />
                </Box>
              </Box>
              )}
              {isMultiCharge && mobileStep === MobileStep.ReviewCharges && (
              <Box data-testid="step-review-charges">
                <ChargesList charges={charges!} isMobileReview />
              </Box>
              )}
            </Box>
          </>
        ) : (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {errors && errors.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="error" classes={{ icon: classes.errorIconContainer }}>
                <Typography variant="subtitle1">{errors}</Typography>
              </Alert>
            </Grid>
            )}
            <Header
              methods={methods}
              charge={selectedCharges.length === 1 ? selectedCharges[0] : undefined}
              accountingEntry={accountingEntry}
              isMultiCharge={isMultiCharge}
            />
            <Box className={classes.containerResponsive}>
              {charges && charges.length > 1 && (
              <ChargesList
                charges={charges}
                selectedIds={selectedChargeIds}
                onToggle={handleChargeToggle}
              />
              )}
              <KnownPartiesList
                parties={orderParties}
                onPartySelect={handlePartySelect}
                partiesSelected={partiesSelected}
                kind={kind}
              />
              <Box className={classes.selectedPartiesContainer}>
                <Box flex={1}>
                  <SelectedPartiesList
                    methods={methods}
                    partiesSelected={partiesSelected}
                    charges={charges}
                    accountingEntry={accountingEntry}
                    hideAtClosingAmount={hideAtClosingAmount}
                    hideBeforeClosingAmount={hideBeforeClosingAmount}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 500, color: theme.palette.primary.light, marginBottom: '5px' }}>
                    {t('charges:partySearchAndCreation')}
                  </Typography>
                  <SearchPartyInput
                    className={classes.partyInput}
                    clearTextOnSelect
                    onSelectParty={(party) => {
										  if (party) {
										    handlePartySelect({
										      _id: party._id,
										      name: party.name,
										      kinds: ['']
										    } as NewOrderPartyType);
										  }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      {isMobile ? (
        <MobileStepNavigation
          mobileStep={mobileStep}
          isMultiCharge={isMultiCharge}
          loadingSave={loadingSave}
          onNext={goToNextStep}
          onBack={goToPrevStep}
          onSave={handleOnSave}
          onClose={onClose}
        />
      ) : (
        <DialogActions>
          <Grid container direction="row" justifyContent="flex-end" spacing={2}>
            <Grid item>
              <BaseButton
                disabled={loadingSave}
                isLoading={false}
                customStyle={{ fontSize: "10px !important" }}
                buttonVariant="contained"
                text={t('common:close', { defaultValue: 'Close' })}
                onClick={onClose}
              />
            </Grid>
            <Grid item>
              <BaseButton
                buttonVariant="contained"
                text={t('common:save', { defaultValue: 'Save' })}
                onClick={handleOnSave}
                disabled={loadingSave}
                isLoading={loadingSave}
              />
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SplitPayeePayerScene;
